import { useCallback, useEffect, useRef, useState } from "react";
import Toolbar from "./Toolbar";
import { CV } from "@/config/canvasSizing";
import { SB } from "@/config/sidebarSizing";

export interface ToolbarPos {
  x: number;
  y: number;
}

const STORAGE_KEY = "kf:toolbarPos";

// Where the user last dropped the bar. Invalid / absent → null (render at the
// top-center default). Wrapped in try/catch so a corrupt entry or a disabled
// localStorage never breaks the canvas.
export function loadToolbarPos(): ToolbarPos | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as unknown;
    if (p && typeof (p as ToolbarPos).x === "number" && typeof (p as ToolbarPos).y === "number") {
      return { x: (p as ToolbarPos).x, y: (p as ToolbarPos).y };
    }
    return null;
  } catch {
    return null;
  }
}

export function saveToolbarPos(pos: ToolbarPos): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    /* localStorage unavailable (private mode / quota) — position just won't persist */
  }
}

// Keep the bar fully on-screen with a small margin. Clamps on every drag frame and
// again on resize, so shrinking the window can never strand the bar off-screen. If
// the bar is wider/taller than the viewport, the max floors to `margin` (top-left
// wins over hiding it entirely).
export function clampToolbarPos(
  pos: ToolbarPos,
  size: { w: number; h: number },
  viewport: { vw: number; vh: number },
  margin: number = CV.toolbar.dragMargin,
): ToolbarPos {
  const maxX = Math.max(margin, viewport.vw - size.w - margin);
  const maxY = Math.max(margin, viewport.vh - size.h - margin);
  return {
    x: Math.min(Math.max(pos.x, margin), maxX),
    y: Math.min(Math.max(pos.y, margin), maxY),
  };
}

// Desktop-only floating tool bar: starts top-center over the canvas (mock parity)
// and can be dragged anywhere on screen by its grip. Position persists across
// reloads. Rendered as a fixed overlay in AppShell (inside ReactFlowProvider so the
// inner Toolbar can still call useReactFlow for node placement).
export default function FloatingToolbar() {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<ToolbarPos | null>(() => loadToolbarPos());
  // Pointer offset from the bar's top-left at drag start, so the bar tracks the
  // cursor without snapping its corner to the pointer.
  const dragOffset = useRef<{ dx: number; dy: number } | null>(null);

  // Re-clamp a saved position on mount and whenever the window resizes.
  useEffect(() => {
    const reclamp = () => {
      const el = ref.current;
      if (!el) return;
      setPos((prev) => {
        if (!prev) return prev;
        const r = el.getBoundingClientRect();
        return clampToolbarPos(prev, { w: r.width, h: r.height }, { vw: window.innerWidth, vh: window.innerHeight });
      });
    };
    reclamp();
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, []);

  const onGripPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    dragOffset.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    // Freeze the current (possibly CSS-centered) position as explicit px so the
    // first drag frame doesn't jump from the translateX(-50%) default.
    setPos({ x: r.left, y: r.top });

    const onMove = (ev: PointerEvent) => {
      const off = dragOffset.current;
      const box = ref.current?.getBoundingClientRect();
      if (!off || !box) return;
      setPos(clampToolbarPos(
        { x: ev.clientX - off.dx, y: ev.clientY - off.dy },
        { w: box.width, h: box.height },
        { vw: window.innerWidth, vh: window.innerHeight },
      ));
    };
    const onUp = () => {
      dragOffset.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      // Persist wherever it landed (functional read so we save the latest value).
      setPos((p) => {
        if (p) saveToolbarPos(p);
        return p;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  // Default (never dragged): centered over the canvas region at the top — the rail
  // is a fixed-width spacer, so `50% + half-rail` lands on the canvas midline (mock
  // `left: calc(50% + 29px)`). Once dragged, switch to explicit px (no transform).
  const style: React.CSSProperties = pos
    ? { position: "fixed", left: pos.x, top: pos.y, zIndex: CV.toolbar.floatZ }
    : {
        position: "fixed",
        left: `calc(50% + ${SB.rail.collapsed / 2}px)`,
        top: CV.toolbar.floatTop,
        transform: "translateX(-50%)",
        zIndex: CV.toolbar.floatZ,
      };

  return (
    <div ref={ref} data-testid="floating-toolbar" style={style}>
      <Toolbar isMobile={false} onGripPointerDown={onGripPointerDown} />
    </div>
  );
}
